import { contactBlock } from "./legal";

export const privacyPolicy = {
  title: "Privacy Policy",
  updated: "14 September 2026",
  heroCopy:
    "This Privacy Policy explains how Bytevora IT Solutions Private Limited collects, uses, stores, shares, and protects personal information when you visit our website, contact us, or interact with our services.",
  toc: [
    { label: "Information We Collect", id: "information-we-collect" },
    { label: "How We Use Information", id: "how-we-use-your-information" },
    { label: "Cookies", id: "cookies-and-similar-technologies" },
    { label: "Sharing", id: "how-we-share-information" },
    { label: "Payments", id: "payments" },
    { label: "Data Retention", id: "data-retention" },
    { label: "Your Rights", id: "your-rights" },
    { label: "Security", id: "data-security" },
    { label: "Contact", id: "contact-us" },
  ],
  intro: [
    "Bytevora IT Solutions Private Limited (“Bytevora”, “we”, “us”, or “our”) respects your privacy and is committed to handling personal information responsibly.",
    "This Privacy Policy explains how we collect, use, disclose, store, and protect personal information when you visit our website, contact us, request information about our services, enter into a business relationship with us, or otherwise interact with Bytevora.",
    "By using our website or providing personal information to us, you acknowledge the practices described in this Privacy Policy.",
  ],
  sections: [
    {
      id: "information-we-collect",
      title: "Information We Collect",
      blocks: [
        { type: "p", text: "We may collect personal information that you provide directly to us, including:" },
        {
          type: "list",
          items: [
            "Name",
            "Email address",
            "Phone number",
            "Company or organization",
            "Project information",
            "Service interests",
            "Messages or enquiries",
            "Billing or business information",
            "Other information you choose to provide",
          ],
        },
        { type: "p", text: "We may also collect technical information when you visit our website, such as browser type, device information, IP address, pages visited, session information, technical logs, and cookie preferences where applicable." },
      ],
    },
    {
      id: "how-we-use-your-information",
      title: "How We Use Your Information",
      blocks: [
        { type: "p", text: "We may use personal information to:" },
        {
          type: "list",
          items: [
            "Respond to enquiries",
            "Discuss potential projects",
            "Prepare quotations or proposals",
            "Provide services",
            "Communicate with clients",
            "Manage commercial relationships",
            "Process billing and payments",
            "Provide support",
            "Improve website functionality",
            "Maintain security",
            "Prevent misuse or fraud",
            "Meet legal obligations",
            "Maintain business records",
          ],
        },
      ],
    },
    {
      id: "cookies-and-similar-technologies",
      title: "Cookies and Similar Technologies",
      blocks: [
        { type: "p", text: "Bytevora may use cookies and similar technologies to operate the website, remember preferences, understand website usage, improve performance, and support third-party functionality." },
        { type: "p", text: "Where required, non-essential cookies will only be used with appropriate consent." },
        {
          type: "p",
          segments: [
            { t: "For more information, please review our " },
            { link: "Cookie Policy", to: "/cookie-policy" },
            { t: "." },
          ],
        },
      ],
    },
    {
      id: "how-we-share-information",
      title: "How We Share Information",
      blocks: [
        { type: "p", text: "Bytevora does not sell personal information to advertisers." },
        { type: "p", text: "We may share information where reasonably necessary with:" },
        {
          type: "list",
          items: [
            "Hosting and cloud providers",
            "Payment providers",
            "Email or communication services",
            "Analytics providers",
            "CRM or business software providers",
            "Security providers",
            "Professional advisers",
            "Contractors supporting our services",
          ],
        },
        { type: "p", text: "We may also disclose information where required by law or to protect legal rights, safety, or property." },
      ],
    },
    {
      id: "payments",
      title: "Payments",
      blocks: [
        { type: "p", text: "Where online payments are accepted, transactions may be processed by third-party payment service providers." },
        { type: "p", text: "These providers may collect payment information directly in accordance with their own privacy policies." },
        { type: "p", text: "Bytevora may receive limited transaction details such as payment status, reference number, amount, currency, and billing information." },
        { type: "p", text: "We do not intend to store complete card credentials, CVV information, UPI PINs, or online banking credentials on our website." },
      ],
    },
    {
      id: "data-retention",
      title: "Data Retention",
      blocks: [
        { type: "p", text: "We retain personal information only for as long as reasonably necessary for business, contractual, legal, tax, security, or compliance purposes." },
        { type: "p", text: "When information is no longer required, we may delete, anonymize, or securely archive it as appropriate." },
      ],
    },
    {
      id: "data-security",
      title: "Data Security",
      blocks: [
        { type: "p", text: "Bytevora uses reasonable technical and organizational measures designed to protect personal information." },
        { type: "p", text: "However, no internet transmission or electronic storage system can be guaranteed to be completely secure." },
      ],
    },
    {
      id: "your-rights",
      title: "Your Rights",
      blocks: [
        { type: "p", text: "Depending on applicable law, you may have rights to:" },
        {
          type: "list",
          items: [
            "Access your personal information",
            "Correct inaccurate information",
            "Request deletion",
            "Object to certain processing",
            "Restrict certain uses",
            "Withdraw consent",
            "Request information about data use",
            "Submit a privacy complaint",
          ],
        },
        { type: "p", text: "Some rights may be subject to legal exceptions or retention requirements. To make a privacy request, contact us using the details provided at the end of this Privacy Policy. We may need to verify your identity before responding to certain requests." },
      ],
    },
    {
      id: "third-party-services",
      title: "Third-Party Services",
      blocks: [
        { type: "p", text: "The website may use third-party services for hosting, payments, analytics, security, communications, or other website functionality." },
        { type: "p", text: "These third parties may process information under their own privacy policies." },
      ],
    },
    {
      id: "international-data-transfers",
      title: "International Data Transfers",
      blocks: [
        { type: "p", text: "Some providers used by Bytevora may process information outside your country." },
        { type: "p", text: "Where applicable, we take reasonable steps intended to protect personal information when such transfers occur." },
      ],
    },
    {
      id: "marketing-communications",
      title: "Marketing Communications",
      blocks: [
        { type: "p", text: "Where permitted, Bytevora may send service-related or marketing communications." },
        { type: "p", text: "You may opt out of marketing messages at any time by using the unsubscribe option provided in the communication or by contacting us." },
        { type: "p", text: "Transactional or contractual communications may still be sent where required." },
      ],
    },
    {
      id: "childrens-privacy",
      title: "Children’s Privacy",
      blocks: [
        { type: "p", text: "Bytevora’s website and services are primarily intended for business and professional users." },
        { type: "p", text: "We do not knowingly collect personal information from children where parental consent would be legally required. If you believe a child has provided personal information to us improperly, please contact us so we can review and address the matter." },
      ],
    },
    {
      id: "external-links",
      title: "External Links",
      blocks: [
        { type: "p", text: "Our website may contain links to third-party websites." },
        { type: "p", text: "Bytevora is not responsible for the privacy practices or security of external websites. We recommend reviewing the privacy policies of third-party websites before providing them with personal information." },
      ],
    },
    {
      id: "changes-to-this-privacy-policy",
      title: "Changes to This Privacy Policy",
      blocks: [
        { type: "p", text: "We may update this Privacy Policy periodically to reflect changes in our website, services, business practices, third-party providers, technology, or legal requirements." },
        { type: "p", text: "The latest version will be published on this page with an updated revision date. We encourage you to review this Privacy Policy periodically." },
      ],
    },
    {
      id: "contact-us",
      title: "Contact Us",
      blocks: [
        { type: "p", text: "For privacy-related questions or requests, please contact:" },
        contactBlock,
      ],
    },
  ],
};
